import api from './api';

class MasterDataService {
  // Get all categories with counts
  static async getCategories() {
    try {
      const response = await api.get('/master-data');
      return response.data;
    } catch (error) {
      console.error('Error fetching master data categories:', error);
      throw this.handleError(error);
    }
  }

  // Get all master data for dropdowns
  static async getDropdowns() {
    try {
      const response = await api.get('/master-data/dropdowns');
      return response.data;
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
      throw this.handleError(error);
    }
  }

  // Get contract-specific dropdown data
  static async getContractDropdowns() {
    try {
      const categories = [
        'ContractType',
        'Status', 
        'Priority',
        'Currency',
        'PaymentTerms',
        'Frequency',
        'Department',
        'Location'
      ];

      const dropdownData = {};
      
      // Add delay between requests to avoid rate limiting
      for (let i = 0; i < categories.length; i++) {
        const category = categories[i];
        try {
          const response = await this.getByCategory(category);
          dropdownData[category] = response.data || [];
          
          // Add delay between requests (except for the last one)
          if (i < categories.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 200)); // 200ms delay
          }
        } catch (error) {
          console.warn(`Failed to fetch ${category}:`, error);
          dropdownData[category] = [];
          
          // If it's a rate limit error, wait longer before next request
          if (error.response?.status === 429) {
            console.log('Rate limit hit, waiting 2 seconds before next request...');
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
      }

      return {
        success: true,
        data: dropdownData
      };
    } catch (error) {
      console.error('Error fetching contract dropdown data:', error);
      throw this.handleError(error);
    }
  }

  // Get master data by category
  static async getByCategory(category, parentValue = null) {
    try {
      const params = parentValue ? { parentValue } : {};
      const response = await api.get(`/master-data/${category}`, { params });
      
      // If backend returns success but empty data, provide hardcoded fallback
      if (category === 'PROJECTCATEGORY' && (!response.data?.data || response.data.data.length === 0)) {
        return {
          success: true,
          data: [
            { code: 'software', label: 'Software Development', isActive: true },
            { code: 'infrastructure', label: 'Infrastructure', isActive: true },
            { code: 'marketing', label: 'Marketing', isActive: true }
          ]
        };
      }
      
      return response.data;
    } catch (error) {
      // If the 404 happens for the category, return the fallback here too
      if (category === 'PROJECTCATEGORY') {
        return { success: true, data: [{ code: 'general', label: 'General Project', isActive: true }] };
      }
      throw this.handleError(error);
    }
  }

  // Get master data item by ID
  static async getById(id) {
    try {
      const response = await api.get(`/master-data/item/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching master data item:', error);
      throw this.handleError(error);
    }
  }

  // Create new master data item
  static async create(data) {
    try {
      const response = await api.post('/master-data', data);
      return response.data;
    } catch (error) {
      console.error('Error creating master data item:', error);
      const handledError = this.handleError(error);
      // Convert to Error instance for better error handling
      const err = new Error(handledError.message || 'Failed to create master data item');
      err.type = handledError.type;
      err.errors = handledError.errors;
      throw err;
    }
  }

  // Update master data item
  static async update(id, data) {
    try {
      const response = await api.put(`/master-data/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating master data item:', error);
      const handledError = this.handleError(error);
      // Convert to Error instance for better error handling
      const err = new Error(handledError.message || 'Failed to update master data item');
      err.type = handledError.type;
      err.errors = handledError.errors;
      throw err;
    }
  }

  // Delete master data item
  static async delete(id) {
    try {
      const response = await api.delete(`/master-data/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting master data item:', error);
      const handledError = this.handleError(error);
      // Convert to Error instance for better error handling
      const err = new Error(handledError.message || 'Failed to delete master data item');
      err.type = handledError.type;
      err.errors = handledError.errors;
      throw err;
    }
  }

  // Bulk create master data items
  static async bulkCreate(items) {
    try {
      const response = await api.post('/master-data/bulk', { items });
      return response.data;
    } catch (error) {
      console.error('Error bulk creating master data items:', error);
      throw this.handleError(error);
    }
  }

  // Reorder items in category
  static async reorder(category, items) {
    try {
      const response = await api.put(`/master-data/${category}/reorder`, { items });
      return response.data;
    } catch (error) {
      console.error('Error reordering master data items:', error);
      throw this.handleError(error);
    }
  }

  // Seed initial master data
  static async seedInitialData() {
    try {
      const response = await api.post('/master-data/seed');
      return response.data;
    } catch (error) {
      console.error('Error seeding initial master data:', error);
      throw this.handleError(error);
    }
  }

  // Handle API errors
  static handleError(error) {
    if (error.response) {
      const { status, data } = error.response;
      
      if (status === 400) {
        return {
          type: 'validation',
          message: data.message || 'Validation failed',
          errors: data.errors || []
        };
      } else if (status === 401) {
        return {
          type: 'auth',
          message: 'Authentication required'
        };
      } else if (status === 403) {
        return {
          type: 'permission',
          message: 'Insufficient permissions'
        };
      } else if (status === 404) {
        return {
          type: 'not_found',
          message: 'Master data item not found'
        };
      } else if (status === 409) {
        return {
          type: 'conflict',
          message: data.message || 'Conflict occurred'
        };
      } else if (status >= 500) {
        return {
          type: 'server',
          message: 'Server error occurred'
        };
      }
    } else if (error.request) {
      return {
        type: 'network',
        message: 'Network error - please check your connection'
      };
    } else {
      return {
        type: 'unknown',
        message: error.message || 'An unexpected error occurred'
      };
    }
  }

  // Utility methods
  static getCategoryDisplayName(category) {
    const categoryNames = {
      'ContractType': 'Contract Types',
      'RiskLevel': 'Risk Levels',
      'Status': 'Statuses',
      'Priority': 'Priorities',
      'PaymentTerms': 'Payment Terms',
      'Frequency': 'Frequencies',
      'Currency': 'Currencies',
      'Department': 'Departments',
      'Location': 'Locations',
      'ApprovalLevel': 'Approval Levels',
      'NoticePeriod': 'Notice Periods',
      'GoverningLaw': 'Governing Laws',
      'Jurisdiction': 'Jurisdictions',
      'ClauseCategory': 'Clause Categories',
      'CounterpartyType': 'Counterparty Types',
      'AssetType': 'Asset Types',
      'AssetSubtype': 'Asset Subtypes',
      'DepreciationMethod': 'Depreciation Methods',
      'AssetStatus': 'Asset Statuses'
    };
    
    // If category is in the predefined list, return the display name
    if (categoryNames[category]) {
      return categoryNames[category];
    }
    
    // For custom categories, convert camelCase to Title Case with spaces
    // e.g., "CustomCategory" -> "Custom Category"
    return category.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
  }

  static getCategoryDescription(category) {
    const descriptions = {
      'ContractType': 'Types of contracts used in the system',
      'RiskLevel': 'Risk levels for contract assessment',
      'Status': 'Contract status values',
      'Priority': 'Contract priority levels',
      'PaymentTerms': 'Payment terms for contracts',
      'Frequency': 'Payment frequencies',
      'Currency': 'Supported currencies',
      'Department': 'Organizational departments',
      'Location': 'Geographic locations',
      'ApprovalLevel': 'Approval hierarchy levels',
      'NoticePeriod': 'Notice periods for termination',
      'GoverningLaw': 'Governing laws for contracts',
      'Jurisdiction': 'Legal jurisdictions',
      'ClauseCategory': 'Categories for legal clauses',
      'CounterpartyType': 'Types of counterparties'
    };
    return descriptions[category] || 'Master data category';
  }

  static isDependentCategory(category) {
    return ['Jurisdiction'].includes(category);
  }

  static getParentCategory(category) {
    const parentCategories = {
      'Jurisdiction': 'GoverningLaw'
    };
    return parentCategories[category];
  }
}

export default MasterDataService;
